import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { generateRandomNumber } from 'src/helpers/CommonHelpers';
import { checkSparkDataAvailable } from 'src/helpers/validationHelper';
import {
  isPdfFloorplan,
  resolveFloorplanFiles,
  resolveFloorplanImage,
  toNameOptions,
  keyFobsNeedLabels,
  withKeyFobLabels,
  mapFormAreas,
  FloorplanFile,
  SuiteOption,
  NamedItem,
} from '../helpers';
import { useSuiteFormUIState } from './useSuiteFormUIState';
import {
  useSuiteFormEntityState,
  UseSuiteFormEntityStateReturn,
} from './useSuiteFormEntityState';
import { useSuiteStatuses } from './useSuiteStatuses';
import {
  usePropertiesAPI,
  UsePropertiesAPIReturn,
} from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';
import { useDocumentUpload } from 'src/pages/SharedPageCustomHooks/UseDocumentUpload/useDocumentUpload';

const DEFAULT_STATUS_ID = '00000000-0000-0000-0000-000000000000';

interface UseSuiteFormOrchestratorParams {
  initialSuite: any;
  onClose: () => void;
  onSaveSuite: (
    formData: any,
    areasData: any,
    floorImage: any
  ) => Promise<{ ok: boolean }>;
  confirmOnSave?: boolean;
}

export interface UseSuiteFormOrchestratorReturn {
  suiteFormData: any;
  suiteStatuses: any[];
  statusOptions: any[];
  selectedStatusOption: any;
  user: any;
  state: any;
  suiteKeysAndValues: any[];
  isSparkDisabled: boolean;

  floorplanFiles: any[];
  floorplanImage: any;
  showPdfFloorplanLink: boolean;
  isFloorplanStaged: boolean;
  stagedFloorplanName: string;
  buildingOptions: any[];
  levelOptions: any[];
  selectedBuildingOption: any;
  selectedLevelOption: any;

  onSetSuiteFormData: (data: any) => void;
  onToggleCommonArea: () => void;
  onEditorChange: (editorState: any) => void;
  onSave: (actionType: string) => Promise<void>;
  onAddAnother: () => void;
  onValidateFile: (file: any) => void;
  onClearFloorplan: () => void;
  handleSelectedStatus: (option: any) => void;
  onChangeBuilding: (option: any) => void;
  onChangeLevel: (option: any) => void;
  onChangeOrientationDate: (date: Date | null) => void;
  onChangeCompletionDate: (date: Date | null) => void;
  onChangePossessionDate: (date: Date | null) => void;
  onCheckExistingParkingStallStorageLockers: (
    newValue: any,
    latestValue: any,
    type: string
  ) => Promise<void>;
  onAddPair: () => void;
  onRemovePair: (item: any) => void;
  onInputChange: (value: any, item: any, property: string) => void;
}

export const useSuiteFormOrchestrator = ({
  initialSuite,
  onClose,
  onSaveSuite,
  confirmOnSave,
}: UseSuiteFormOrchestratorParams): UseSuiteFormOrchestratorReturn => {
  const user = useSelector(selectAuthenticatedUser);

  const {
    suiteFormData,
    updateSuiteFormData: onSetSuiteFormData,
    resetSuiteFormData,
  }: UseSuiteFormEntityStateReturn = useSuiteFormEntityState(initialSuite);

  const suiteStatuses: any[] = useSuiteStatuses();

  const {
    buildings: buildingList,
    fetchBuildingForProperty,
    levels: levelList,
    fetchLevelsForBuilding,
    checkSlotAvailability,
  }: UsePropertiesAPIReturn = usePropertiesAPI();

  const { validateAndProcessFiles } = useDocumentUpload({
    documentType: 'Floorplan',
  });

  const { uiState: state, patchUiState } = useSuiteFormUIState({
    suiteFormData,
  });

  const onEditorChange = (editorState: any) => {
    patchUiState({ editorState });

    onSetSuiteFormData({
      keyHandOffInstructions: editorState,
    });
  };

  const onToggleCommonArea = () => {
    onSetSuiteFormData({ isCommonArea: !suiteFormData.isCommonArea });
  };

  const onMount = async () => {
    if (
      suiteFormData.id &&
      suiteFormData.keyFobs !== null &&
      keyFobsNeedLabels(suiteFormData.keyFobs)
    ) {
      onSetSuiteFormData({
        keyFobs: withKeyFobLabels(suiteFormData.keyFobs),
      });
    }

    onAddPair();

    if (suiteFormData.propertyId) {
      fetchBuildingForProperty(suiteFormData.propertyId);
    }

    if (suiteFormData.id) {
      // legacy/workaround: on edit-load these arrive as NamedItem[]; they become
      // the creatable-select shape once converted here for the form.
      const parkingStalls: SuiteOption[] = toNameOptions(
        suiteFormData.parkingStalls as NamedItem[]
      );

      const storageLockers: SuiteOption[] = toNameOptions(
        suiteFormData.storageLockers as NamedItem[]
      );

      const keyFobs: SuiteOption[] = toNameOptions(
        suiteFormData.keyFobs as NamedItem[]
      );

      onSetSuiteFormData({
        parkingStalls,
        storageLockers,
        keyFobs,
      });

      const areasData: any[] = mapFormAreas(suiteFormData.areas);

      patchUiState({
        areas: areasData,
      });

      if (suiteFormData.buildingId) {
        await fetchLevelsForBuilding(suiteFormData.buildingId);

        onSetSuiteFormData({
          BuildingId: suiteFormData.buildingId,
        });
      }
    } else {
      onSetSuiteFormData({
        ...suiteFormData,
        statusId: DEFAULT_STATUS_ID,
      });
    }
  };

  const onSave = async (actionType: string) => {
    patchUiState({ loading: true });

    const areasData: any = state.areas.filter(
      (item: any) => item.other !== 'Other'
    );

    const keyfobs: any =
      suiteFormData.keyFobs && suiteFormData.keyFobs.length > 0
        ? suiteFormData.keyFobs.map((item: any) => {
            return {
              Name: item.value,
            };
          })
        : [];

    const suiteKeysAndValues: any =
      suiteFormData.suiteKeysAndValues &&
      suiteFormData.suiteKeysAndValues.length > 0
        ? suiteFormData.suiteKeysAndValues
        : [];

    const filtered: any = suiteKeysAndValues.filter(function (el: any) {
      return el.key?.trim() && el.value?.trim();
    });

    const updatedData: any = filtered.map(({ id, ...rest }: any) => rest);

    const formData = {
      ...suiteFormData,
      keyFobs: keyfobs,
      suiteKeysAndValues: updatedData,
    };

    const result = await onSaveSuite(formData, areasData, state.floorImage);

    patchUiState({ loading: false });

    if (!result.ok) {
      return;
    }

    // Property "Add Suite" flow lands on an in-dialogue success step instead of
    // closing, so the user can add another or finish.
    if (confirmOnSave) {
      patchUiState({ showConfirmation: true });
      return;
    }

    if (actionType === 'SaveAndAdd') {
      resetForNextSuite();
      return;
    }

    onClose();
  };

  const onAddAnother = () => {
    resetForNextSuite();
    patchUiState({ showConfirmation: false });
  };

  const onValidateFile = (file: any) => {
    const [validFile] = validateAndProcessFiles(file);

    if (!validFile) {
      return;
    }

    onSetSuiteFormData({ floorPlanURL: file });
    patchUiState({
      floorImage: '',
      errors: { ...state.errors, floorPlanURL: '' },
    });
  };

  const onClearFloorplan = () => {
    onSetSuiteFormData({ floorPlanURL: null });
    patchUiState({
      floorImage: null,
      errors: { ...state.errors, floorPlanURL: '' },
    });
  };

  const handleSelectedStatus = (option: any) => {
    onSetSuiteFormData({
      statusId: option?.value ?? DEFAULT_STATUS_ID,
    });
  };

  const onChangeBuilding = (option: any) => {
    onSetSuiteFormData({
      Building: option ? option.label : '',
      BuildingId: option ? option.value : null,
      buildingName: option ? option.label : '',
      Level: '',
      LevelId: null,
      levelName: '',
    });

    if (option) {
      fetchLevelsForBuilding(option.value);
    }
  };

  const onChangeLevel = (option: any) => {
    onSetSuiteFormData({
      LevelId: option ? option.value : null,
      Level: option ? option.label : '',
      levelName: option ? option.label : '',
    });
  };

  const onChangeOrientationDate = (date: Date | null) => {
    patchUiState({ orientationDate: date });
    onSetSuiteFormData({ orientationDate: date });
  };

  const onChangeCompletionDate = (date: Date | null) => {
    patchUiState({ completionDate: date });
    onSetSuiteFormData({ completionDate: date });
  };

  const onChangePossessionDate = (date: Date | null) => {
    patchUiState({ posessionDate: date });
    onSetSuiteFormData({ posessionDate: date });
  };

  const onCheckExistingParkingStallStorageLockers = async (
    newValue: any,
    latestValue: any,
    type: string
  ) => {
    const setStall = (value: any) => {
      if (type === 'parkingStalls') {
        onSetSuiteFormData({ parkingStalls: value });
      } else {
        onSetSuiteFormData({ storageLockers: value });
      }
    };

    const slotName = latestValue?.option?.value;

    if (!slotName) {
      setStall(newValue);
      return;
    }

    patchUiState({ checkingSlotType: type });

    const { ok, available } = await checkSlotAvailability(
      suiteFormData.propertyId,
      slotName,
      type === 'parkingStalls' ? 'ParkingStalls' : 'StorageLockers'
    );

    patchUiState({ checkingSlotType: null });

    if (!ok) {
      return;
    }

    if (!available) {
      toast.error(
        type === 'parkingStalls'
          ? 'This Parking stall is not available.'
          : 'This Storage locker is not available.',
        { autoClose: 12000, position: 'top-center' }
      );
      return;
    }

    setStall(newValue);
  };

  const onAddPair = () => {
    const suiteKeysAndValues = suiteFormData.suiteKeysAndValues
      ? suiteFormData.suiteKeysAndValues
      : [];

    onSetSuiteFormData({
      suiteKeysAndValues: [
        ...suiteKeysAndValues,
        {
          id: generateRandomNumber(),
          key: '',
          value: '',
        },
      ],
    });
  };

  const onRemovePair = (item: any) => {
    const suiteKeysAndValues: any = suiteFormData.suiteKeysAndValues
      ? suiteFormData.suiteKeysAndValues
      : [];

    let updatedData: any = suiteKeysAndValues.filter(
      (object: any) => object.id !== item.id
    );

    onSetSuiteFormData({
      suiteKeysAndValues: updatedData,
    });
  };

  const onInputChange = (value: any, item: any, property: string) => {
    let suiteKeysAndValues: any = suiteFormData.suiteKeysAndValues;
    let updatedData: any = suiteKeysAndValues.map((object: any) => {
      if (object.id === item.id) {
        return { ...item, [property]: value };
      }

      return object;
    });

    onSetSuiteFormData({
      suiteKeysAndValues: updatedData,
    });
  };

  useEffect(() => {
    onMount();
  }, []);

  const resetForNextSuite = () => {
    resetSuiteFormData({
      propertyId: suiteFormData.propertyId,
      statusId: DEFAULT_STATUS_ID,
      suiteKeysAndValues: [{ id: generateRandomNumber(), key: '', value: '' }],
    });

    patchUiState({
      areas: [],
      floorImage: null,
      editorState: '',
    });
  };

  useEffect(() => {
    if (buildingList.length === 0 || !suiteFormData.buildingId) {
      return;
    }

    const matchedBuilding: any = buildingList.find(
      (building: any) => building.id === suiteFormData.buildingId
    );

    if (!matchedBuilding) {
      return;
    }

    onSetSuiteFormData({
      Building: matchedBuilding.building,
      BuildingId: suiteFormData.buildingId,
      buildingName: matchedBuilding.building,
    });
  }, [buildingList, suiteFormData.buildingId]);

  useEffect(() => {
    const url = suiteFormData.floorPlanURL;

    patchUiState({
      floorImage: typeof url === 'string' ? url : null,
    });
  }, [suiteFormData.floorPlanURL]);

  useEffect(() => {
    patchUiState({
      editorState: suiteFormData.keyHandOffInstructions
        ? suiteFormData.keyHandOffInstructions
        : '',
    });
  }, [suiteFormData.keyHandOffInstructions]);

  useEffect(() => {
    if (levelList.length === 0 || !suiteFormData.levelId) {
      return;
    }

    const matchedLevel: any = levelList.find(
      (level: any) => level.id === suiteFormData.levelId
    );

    if (!matchedLevel) {
      return;
    }

    onSetSuiteFormData({
      LevelId: suiteFormData.levelId,
      Level: matchedLevel.level,
      levelName: matchedLevel.level,
    });
  }, [
    levelList,
    suiteFormData.levelId,
    suiteFormData.LevelId,
    suiteFormData.Level,
    suiteFormData.BuildingId,
  ]);

  const suiteKeysAndValues: any =
    suiteFormData.suiteKeysAndValues &&
    suiteFormData.suiteKeysAndValues.length > 0
      ? suiteFormData.suiteKeysAndValues
      : [];

  const floorplanFiles: FloorplanFile[] = resolveFloorplanFiles(
    state.floorImage,
    suiteFormData.floorPlanURL
  );

  const floorplanImage: any = resolveFloorplanImage(state.floorImage);
  const showPdfFloorplanLink: boolean = isPdfFloorplan(state.floorImage);

  const isFloorplanStaged: boolean =
    Array.isArray(suiteFormData.floorPlanURL) &&
    suiteFormData.floorPlanURL.length > 0;

  const stagedFloorplanName: string = isFloorplanStaged
    ? suiteFormData.floorPlanURL[0].name ?? suiteFormData.floorPlanURL[0].path
    : '';

  const buildingOptions = buildingList.map((building: any) => ({
    value: building.id,
    label: building.building,
  }));

  const levelOptions: any = levelList.map((level: any) => ({
    value: level.id,
    label: level.level,
  }));

  const selectedBuildingId: any =
    suiteFormData.BuildingId ?? suiteFormData.buildingId ?? null;

  const selectedBuildingOption: any =
    buildingOptions.find((option: any) => option.value === selectedBuildingId) ??
    null;

  const selectedLevelId: any =
    suiteFormData.LevelId ?? suiteFormData.levelId ?? null;

  const selectedLevelOption: any =
    levelOptions.find((option: any) => option.value === selectedLevelId) ?? null;

  const isSparkDisabled: boolean = checkSparkDataAvailable(
    suiteFormData,
    user.roles[0].role.name
  );

  const statusOptions: any = suiteStatuses.map((status: any) => ({
    value: status.id,
    label: status.name,
  }));

  const hasSelectedStatus: boolean =
    suiteFormData.statusId && suiteFormData.statusId !== DEFAULT_STATUS_ID;

  const matchedStatusOption: any =
    statusOptions.find(
      (option: any) => option.value === suiteFormData.statusId
    ) ?? null;

  const selectedStatusOption: any = hasSelectedStatus ? matchedStatusOption : null;

  return {
    suiteFormData,
    suiteStatuses,
    statusOptions,
    selectedStatusOption,
    user,
    state,
    suiteKeysAndValues,
    isSparkDisabled,

    floorplanFiles,
    floorplanImage,
    showPdfFloorplanLink,
    isFloorplanStaged,
    stagedFloorplanName,
    buildingOptions,
    levelOptions,
    selectedBuildingOption,
    selectedLevelOption,

    onSetSuiteFormData,
    onToggleCommonArea,
    onEditorChange,
    onSave,
    onAddAnother,
    onValidateFile,
    onClearFloorplan,
    handleSelectedStatus,
    onChangeBuilding,
    onChangeLevel,
    onChangeOrientationDate,
    onChangeCompletionDate,
    onChangePossessionDate,
    onCheckExistingParkingStallStorageLockers,
    onAddPair,
    onRemovePair,
    onInputChange,
  };
};
